import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { getApiUrl } from '../utils/api';
import { 
  Home, Calendar, Package, Users, MoreHorizontal, Share2, 
  Clock, Utensils, Truck, CreditCard, FileCheck,
  MessageCircle, Settings, LogOut, Menu, X,
  HelpCircle, ChevronRight, Bell, Cloud, FileText, Building2, Compass,
  GraduationCap, Briefcase, Receipt, Thermometer, Target,
  Palette, ShoppingBag, Scale, Sparkles, AlertTriangle, MapPin,
  BookOpen, Banknote, BookMarked, Tag, ShieldCheck, Award, Zap, Plug, Pencil, ChevronUp, ChevronDown, Star, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { canAccess } from '../utils/permissions';
import { SiteSwitcher } from './SiteSwitcher';
import { BillingNavBadge } from './billing/BillingNavBadge';
import { NavObjectPicker, NavCustomizeToggle } from './NavObjectPicker';
import { KitBadge, NAV_KIT } from './da/KitIcon';
import { useBrandingSave, objSrc } from './home/ObjectLibrary';
import { useMenuFavoris } from '../hooks/useMenuFavoris';


// Objets photoréalistes découpés (DA Happn) — clé = path du module, ou label pour les groupes
const NAV_OBJ = {
  '/dashboard': 'orange_whole', '/actions': 'cocktail_spritz', '/integrations': 'nav_integrations', '/tableau-de-bord': 'nav_finance', '/connecteurs-caisse': 'nav_caisse',
  '/cockpit': 'nav_mise_en_route', '/mon-abonnement': 'nav_carte_membre',  '/acces': 'nav_acces',
  '/equipe': 'toque_chef', '/planning': 'nav_planning', '/pointage': 'nav_pointage', '/taches': 'nav_taches', '/mon-parcours': 'sticker_pamplemousse', '/recrutement': 'nav_recrutement',
  'RH': 'nav_rh', '/conges': 'sticker_lunettes', '/documents': 'nav_documents', '/juridique': 'nav_juridique',
  '/recettes-couts': 'nav_fiches', '/stocks': 'nav_stocks', '/dlc': 'sticker_fraise', '/haccp': 'sticker_torchon',
  '/achats': 'nav_fournisseurs', 'Fournisseurs & Achats': 'nav_fournisseurs', '/fournisseurs': 'nav_fournisseurs', '/articles-achetes': 'sticker_tomate', '/prix-achat': 'nav_fournisseurs', '/commandes-recurrentes': 'sticker_herbes', '/commandes': 'nav_commandes', '/materiel': 'sticker_cuillere',
  '/finance': 'nav_finance', '/caisse': 'nav_caisse', '/facturation': 'plateau_inox', '/livraisons': 'nav_livraisons',
  '/social-media': 'nav_social', '/studio-design': 'nav_studio',
  '/chat': 'nav_chat', '/meteo': 'sticker_citron', '/aide': 'nav_aide',
};
const navKey = (item) => item.path || item.label;
// Objets presque blancs (facture, ticket, agenda…) : contour renforcé pour rester lisibles sur fond clair (01.5 · 28/09)
const LIGHT_OBJS = new Set(['nav_commandes', 'nav_tickets', 'nav_planning', 'nav_taches', 'nav_carte_membre', 'obj_baskets', 'sticker_cuillere']);
const HAPPN_TONES = ['sun', 'mint', 'pink', 'lav', 'sky', 'or'];
const NAV_TONES = new Map(); // tons rendus par la sidebar (mode classique) → repris par l'en-tête du module
// Icône « classique » de l'en-tête d'un module : même badge et même couleur que dans la sidebar
export const ModuleClassicBadge = ({ pathname, size = 44 }) => {
  const hit = Object.keys(NAV_KIT).filter((k) => k.startsWith('/')).sort((a, b) => b.length - a.length).find((k) => pathname.includes(k));
  if (!hit || hit === '/dashboard') return null;
  const tone = NAV_TONES.get(hit) || HAPPN_TONES[[...hit].reduce((a, c) => a + c.charCodeAt(0), 0) % HAPPN_TONES.length];
  const t = tiltOf(hit);
  return <KitBadge name={NAV_KIT[hit] || 'home'} size={size} className={`da-nav-badge app-module-badge is-${tone} hidden md:inline-flex`} style={{ '--rot': `${Math.round(t.rot * .6) || 4}deg` }} data-testid="module-badge" />;
};
const tiltOf = (key) => { const h = [...key].reduce((a, c) => a + c.charCodeAt(0), 0); return { rot: (h % 25) - 12, dx: (h % 5) - 2, dy: ((h >> 2) % 5) - 2, dur: 5 + (h % 4), delay: -(h % 7) }; };
// Style d'icônes de navigation : « objets » (DA 360 Resto, objets détourés, par défaut) ou « classique » (badges du kit). Choix mémorisé sur l'appareil.
export const NAV_STYLE_KEY = 'nav_icon_style';
export const NAV_STYLE_EVENT = 'nav-icon-style';
export const getNavStyle = () => (localStorage.getItem(NAV_STYLE_KEY) === 'classique' ? 'classique' : 'objets');
export const setNavStyle = (v) => { localStorage.setItem(NAV_STYLE_KEY, v); window.dispatchEvent(new Event(NAV_STYLE_EVENT)); };
export const useNavStyle = () => {
  const [style, setStyle] = useState(getNavStyle);
  useEffect(() => { const f = () => setStyle(getNavStyle()); window.addEventListener(NAV_STYLE_EVENT, f); return () => window.removeEventListener(NAV_STYLE_EVENT, f); }, []);
  return style;
};

export const NavIcon = ({ item, small, overrides, style, editing, onEdit, idx }) => {
  if (item.path === '/plus-360') return <Plus360Mark size={small ? 11 : 13} testId="sidebar-plus360-mark" className="da-nav-plusmark" />;
  const mode = style || getNavStyle();
  const key = navKey(item);
  if (mode === 'classique') {
    // Happn : tuile pastel (jaune · menthe · rose · lavande · ciel · orange) légèrement penchée, couleur et inclinaison stables par module
    const t = tiltOf(key);
    const tone = HAPPN_TONES[(idx ?? [...key].reduce((a, c) => a + c.charCodeAt(0), 0)) % HAPPN_TONES.length];
    NAV_TONES.set(key, tone);
    return <KitBadge name={NAV_KIT[key] || 'home'} size={small ? 26 : 34} className={`da-nav-badge is-${tone}`} style={{ '--rot': `${Math.round(t.rot * .6) || 4}deg` }} />;
  }
  const obj = overrides?.[key] || NAV_OBJ[key] || 'nav_accueil';
  const t = tiltOf(key);
  const img = <img src={objSrc(obj)} alt="" loading="lazy" className={`da-nav-obj ${small ? 'is-small' : ''} ${LIGHT_OBJS.has(obj) ? 'is-light' : ''}`} style={{ '--rot': `${t.rot}deg`, '--dx': `${t.dx}px`, '--dy': `${t.dy}px`, '--dur': `${t.dur}s`, '--delay': `${t.delay}s` }} />;
  if (!editing || !onEdit) return img;
  return <span role="button" tabIndex={0} className="da-nav-obj-edit" title="Changer l’objet" data-testid={`nav-obj-edit-${key.replace(/[^a-z0-9]+/gi, '-')}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit({ key, label: item.label, current: obj }); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEdit({ key, label: item.label, current: obj }); } }}>{img}<Pencil /></span>;
};

// Bouton haut de sidebar : objets 360 Resto ↔ icônes classiques
export const NavStyleSwitch = ({ compact }) => {
  const style = useNavStyle();
  return <div className={`da-nav-style ${compact ? 'is-compact' : ''}`} role="radiogroup" aria-label="Style des icônes" data-testid="nav-style-switch">
    <button type="button" role="radio" aria-checked={style === 'objets'} className={style === 'objets' ? 'is-on' : ''} onClick={() => setNavStyle('objets')} data-testid="nav-style-objets"><img src="/brand/home/orange_whole.webp" alt="" /> Objets</button>
    <button type="button" role="radio" aria-checked={style === 'classique'} className={style === 'classique' ? 'is-on' : ''} onClick={() => setNavStyle('classique')} data-testid="nav-style-classique"><KitBadge name="home" size={16} /> Classique</button>
  </div>;
};

// Bensimon Logo Component
const BensimonLogo = ({ size = 40, color = '#C46A4A' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <g>
      <path d="M25 15 L25 85" stroke={color} strokeWidth="4" strokeLinecap="round" />
      <path d="M25 15 C25 15, 70 15, 70 32.5 C70 50, 25 50, 25 50" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M25 50 C25 50, 75 50, 75 67.5 C75 85, 25 85, 25 85" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" />
      <line x1="18" y1="32" x2="62" y2="32" stroke={color} strokeWidth="1.5" />
      <line x1="18" y1="67" x2="68" y2="67" stroke={color} strokeWidth="1.5" />
    </g>
  </svg>
);

// Tenant Logo component - renders the appropriate logo based on tenant
const TenantLogo = ({ tenant, size = 40 }) => {
  const [logoKo, setLogoKo] = React.useState(false);
  const logoType = tenant?.branding?.logo_type || tenant?.logo_type || null;
  const primaryColor = tenant?.branding?.primary_color || tenant?.primary_color || 'var(--brand)';
  
  if (logoType === 'svg_b' || tenant?.slug === 'bensimon') {
    return <BensimonLogo size={size} color={primaryColor} />;
  }

  // V5.99.17 — Avocado emoji kept ONLY for tenants who opted-in explicitly
  // (currently only Maison Avocado has `branding.logo_type === 'avocado'`).
  if (logoType === 'avocado') {
    return <span style={{ fontSize: size * 0.75 }}></span>;
  }

  // Default OS branding ("360") if no tenant logo is uploaded
  if (tenant?.branding?.logo_url && !logoKo) {
    const s = Math.round(size * 1.4);
    return <img src={tenant.branding.logo_url} alt={tenant?.name || '360Resto OS'} style={{ width: s, height: s }} className="object-contain shrink-0" data-testid="sidebar-tenant-logo" onError={() => setLogoKo(true)} />;
  }
  if (tenant?.name) {  // LOT 46 v2 · D7 · sans logo (ou logo cassé) : les initiales de l'établissement
    const ini = tenant.name.replace(/DÉMO|DEMO/gi, '').trim().split(/\s+/).filter((w) => !/^(le|la|les|l'|de|du)$/i.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
    return <span className="rounded-full flex items-center justify-center font-extrabold shrink-0" style={{ width: size, height: size, fontSize: size * 0.38, background: primaryColor, color: '#fff' }} aria-label={tenant.name} data-testid="sidebar-tenant-initiales">{ini || '360'}</span>;
  }
  return (
    <span
      className="rounded-xl flex items-center justify-center text-[var(--sur-couleur)] font-extrabold shadow-md"
      style={{
        width: size, height: size,
        fontSize: size * 0.32,
        background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
      }}
      aria-label="Logo"
    >
      360
    </span>
  );
};

import { usePlus360 } from '../hooks/usePlus360';
import { Plus360Mark } from './plus/Plus360';
import { Plus360LockModal } from './plus/Plus360ModuleModal';
import { Lock, Trophy, Hourglass, Gauge } from 'lucide-react';

// TOUS LES MODULES DE L'APPLICATION — 3 groupes de 5 à 7 entrées (charte UX 27/09 : « Au quotidien / Fabrication / Argent ») + « Mon établissement » en bas.
const SIDEBAR_ITEMS = [
  { section: 'main', title: null },
  { icon: Home, label: 'Accueil', path: '/dashboard' },
  { icon: Search, label: 'Explorer', path: '/explorer' },
  { icon: Sparkles, label: 'Actions proposées', path: '/actions' },
  { icon: Gauge, label: 'Tableau de bord', path: '/tableau-de-bord' },

  { section: 'quotidien', title: 'Au quotidien' },
  { icon: Calendar, label: 'Planning', path: '/equipe?vue=planning' },
  { icon: ShieldCheck, label: 'Hygiène', path: '/hygiene' },
  { icon: AlertTriangle, label: 'DLC / Alertes', path: '/dlc' },
  { icon: Users, label: 'Équipe', path: '/equipe' },
  { icon: Cloud, label: 'Météo 15 jours', path: '/meteo' },

  { section: 'fabrication', title: 'Fabrication & stocks' },
  { icon: Utensils, label: 'Recettes & coûts', path: '/recettes-couts' },
  { icon: Package, label: 'Inventaire & Stocks', path: '/stocks' },
  // 28/09 · R1/R5 : le titre ouvre le tableau de bord de la rubrique ; une entrée par objet, dans l'ordre d'usage (01.5)
  { icon: Truck, label: 'Achats', path: '/achats' },
  { icon: Truck, label: 'Uber Eats / Deliveroo', path: '/livraisons', addonKey: 'livraison_360' },

  { section: 'argent', title: 'Argent & visibilité' },
  { icon: Banknote, label: 'Finance', path: '/finance' },
  { icon: FileText, label: 'Rapports', path: '/rapports' },
  { icon: Receipt, label: 'Facturation', path: '/facturation' },
  { icon: Share2, label: 'Réseaux sociaux', path: '/social-media', plusKey: 'social_360' },
  { icon: Palette, label: 'Studio Design', path: '/studio-design', soon: true, plusKey: 'studio_design' },
  { icon: Briefcase, label: 'Recrutement', path: '/recrutement', plusKey: 'recrutement' },
  { icon: Award, label: 'Formation', path: '/formation', plusKey: 'basecamp' },

  { section: 'etablissement', title: 'Mon établissement' },
  { icon: Compass, label: 'Mes infos', path: '/cockpit' },
  { icon: MessageCircle, label: 'Mes experts', path: '/chat' },
  { icon: Plug, label: 'Hub Connexion', path: '/integrations', appStyle: 'hub-integrations', plusKey: 'hub_connexion' },
  { icon: CreditCard, label: 'Mon abonnement Sabril', path: '/mon-abonnement' },
  { icon: Sparkles, label: '360+', path: '/plus-360' },
  { icon: ShieldCheck, label: 'Journal des accès', path: '/acces' },
  { icon: HelpCircle, label: 'Centre d\'aide', path: '/aide' },
];

// PREVIEW (cahier 06.1 · 01/10) — nouvelle arborescence par métier, visible uniquement avec ?menu=v2 ou localStorage menu_v2=1. Aucune donnée migrée : maquette visuelle réversible.
export const SIDEBAR_ITEMS_V2 = [
  { section: 'main', title: null },
  { icon: Home, label: 'Accueil', path: '/dashboard' },
  { icon: Search, label: 'Explorer', path: '/explorer' },
  { icon: Sparkles, label: 'Actions', path: '/actions' },
  { icon: Gauge, label: 'Tableau de bord', path: '/tableau-de-bord' },

  { section: 'favoris', title: 'Favoris' },
  { icon: Calendar, label: 'Planning', path: '/planning' },
  { icon: Receipt, label: 'Commandes & factures', path: '/commandes' },
  { icon: Banknote, label: 'Finance', path: '/finance' },
  { icon: Package, label: 'Inventaire & Stocks', path: '/stocks' },

  { section: 'activite', title: 'Mon activité' },
  { icon: Target, label: 'Piloter', path: '/piloter', children: [
      { icon: Banknote, label: 'Finance', path: '/finance' },
      { icon: FileText, label: 'Rapports', path: '/rapports' },
      { icon: CreditCard, label: 'Caisse', path: '/connecteurs-caisse', hint: 'branchement et synchronisations' },
  ]},
  { icon: Utensils, label: 'Produire', path: '/produire', children: [
      { icon: Utensils, label: 'Recettes & coûts', path: '/recettes-couts' },
      { icon: Package, label: 'Inventaire & Stocks', path: '/stocks' },
      { icon: ShieldCheck, label: 'Hygiène', path: '/hygiene' },
      { icon: AlertTriangle, label: 'DLC', path: '/dlc' },
  ]},
  { icon: Truck, label: 'Achats', path: '/achats' },
  { icon: Users, label: 'Équipe', path: '/equipe', children: [
      { icon: Briefcase, label: 'Recrutement', path: '/recrutement', plusKey: 'recrutement' },
      { icon: Award, label: 'Formation', path: '/formation', plusKey: 'basecamp' },
  ]},
  { icon: Share2, label: 'Communiquer', path: '/communiquer', children: [
      { icon: Plug, label: 'Hub Connexion', path: '/integrations', plusKey: 'hub_connexion' },
      { icon: Share2, label: 'Réseaux sociaux', path: '/social-media', plusKey: 'social_360' },
      { icon: MessageCircle, label: 'Relation client', path: '/relation-client', plusKey: 'relation_client' },
      { icon: Palette, label: 'Studio Design', path: '/studio-design', soon: true, plusKey: 'studio_design' },
  ]},
  { icon: Sparkles, label: '360+', path: '/plus-360' },

  { section: 'etablissement', title: 'Mon établissement' },
  { icon: Settings, label: 'Réglages & abonnement', path: '/cockpit', children: [
      { icon: Compass, label: 'Mes infos', path: '/cockpit' },
      { icon: MessageCircle, label: 'Mes experts', path: '/chat' },
      { icon: CreditCard, label: 'Mon abonnement Sabril', path: '/mon-abonnement' },
      { icon: ShieldCheck, label: 'Journal des accès', path: '/acces' },
  ]},
  { icon: HelpCircle, label: 'Centre d\'aide', path: '/aide' },
];
export const isMenuV2 = (search) => {
  try {
    const m = new URLSearchParams(search || window.location.search).get('menu');
    if (m === 'v1') return false;
    if (m === 'v2') return true;
    return localStorage.getItem('menu_v1') !== '1';
  } catch { return true; }
};


export const ALL_MODULES = SIDEBAR_ITEMS.filter((it) => it.path && !it.soon).flatMap((it) => [it, ...(it.children || [])]).map(({ label, path }) => ({ label, path }));
export const itemKey = (it) => (Array.isArray(it.children) ? `group:${it.label}` : it.path);
const LEGACY_KEYS = { '/configuration': '/cockpit', '/parametres': null, '/tickets': null, '/achats-recurrents': '/commandes-recurrentes' };
const migrateKeys = (keys) => (keys || []).map((k) => (k in LEGACY_KEYS ? LEGACY_KEYS[k] : k)).filter(Boolean).filter((k, i, a) => a.indexOf(k) === i);

// Regroupe la liste plate en rubriques, puis applique l'ordre personnalisé (tenant.branding.nav_order)
export const buildSections = (order, items = SIDEBAR_ITEMS) => {
  const sections = [];
  for (const it of items) {
    if (it.section) sections.push({ key: it.section, title: it.title, items: [] });
    else sections[sections.length - 1].items.push(it);
  }
  const rank = (list, keys) => { const idx = (k) => { const i = (keys || []).indexOf(k); return i === -1 ? 999 : i; }; return [...list].sort((a, b) => idx(a) - idx(b)); };
  const pin = (k) => (k === 'main' ? -1 : k === 'etablissement' ? 1 : 0); // Accueil toujours en tête, « Mon établissement » toujours en bas
  const secKeys = rank(sections.map((s) => s.key), order?.sections).sort((a, b) => pin(a) - pin(b));
  return secKeys.map((k) => { const s = sections.find((x) => x.key === k); const keys = rank(s.items.map(itemKey), migrateKeys(order?.items?.[k])); return { ...s, items: keys.map((ik) => s.items.find((x) => itemKey(x) === ik)) }; });
};

const move = (arr, from, dir) => { const to = from + dir; if (to < 0 || to >= arr.length) return null; const n = [...arr]; [n[from], n[to]] = [n[to], n[from]]; return n; };

// Flèches ▲▼ affichées en mode « Personnaliser » pour réordonner rubriques et modules
const OrderArrows = ({ onUp, onDown, testid, small }) => (
  <span className={`da-nav-order ${small ? 'is-small' : ''}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
    <button type="button" disabled={!onUp} onClick={onUp} aria-label="Monter" data-testid={`${testid}-up`}><ChevronUp size={small ? 12 : 14} /></button>
    <button type="button" disabled={!onDown} onClick={onDown} aria-label="Descendre" data-testid={`${testid}-down`}><ChevronDown size={small ? 12 : 14} /></button>
  </span>
);

/** R4 (28/09) · badge du menu = nombre de sujets d'alerte (Centre d'actions filtré) ; couleur d'alerte si urgent ; détail au survol ; clic → liste ; masqué à 0. */
export const NavBadge = ({ count, to, testId }) => {
  const navigate = useNavigate();
  const n = typeof count === 'number' ? count : count?.n || 0;
  const ref = useRef(null);
  const prev = useRef(n);
  useEffect(() => {
    if (n > prev.current && ref.current) {
      const el = ref.current;
      el.classList.remove('da-nav-count-bump');
      void el.offsetWidth;
      el.classList.add('da-nav-count-bump');
      const t = setTimeout(() => el.classList.remove('da-nav-count-bump'), 1400);
      prev.current = n;
      return () => clearTimeout(t);
    }
    prev.current = n;
    return undefined;
  }, [n]);
  if (!n) return null;
  const urgent = !!count?.urgent;
  const detail = count?.detail || [];
  const title = `${n} alerte${n > 1 ? 's' : ''}${detail.length ? ' : ' + detail.map((d) => d.title).join(' · ') : ''}`;
  const go = (e) => { e.preventDefault(); e.stopPropagation(); navigate(to); };
  return (
    <span ref={ref} role="button" tabIndex={0} onClick={go} onKeyDown={(e) => { if (e.key === 'Enter') go(e); }} className={`da-nav-count ${urgent ? 'is-urgent' : ''}`} aria-label={title} data-testid={testId} data-urgent={urgent ? '1' : undefined}>
      {n > 99 ? '99+' : n}
      <span className="da-nav-tip" role="tooltip" data-testid={testId ? `${testId}-detail` : undefined}>
        <b>{n} alerte{n > 1 ? 's' : ''}</b>
        {detail.map((d) => <i key={d.key} className={d.urgent ? 'is-urgent' : ''}>{d.title}</i>)}
      </span>
    </span>
  );
};

export const DesktopSidebar = ({ isOpen, onClose, collapsed = false, mobileOnly = false }) => {
  const { user, logout } = useAuth();
  const { tenant, tenantSlug: contextTenantSlug } = useTenant();
  const navigate = useNavigate();
  const { tenantSlug: paramTenantSlug } = useParams();
  const location = useLocation();
  const menuV2 = isMenuV2(location.search);
  const itemsSrc = menuV2 ? SIDEBAR_ITEMS_V2 : SIDEBAR_ITEMS;
  const { favoris: favPaths, favSet, toggleFav } = useMenuFavoris(user);
  // Registre path -> item (icône + libellé) pour résoudre les favoris dynamiques
  const moduleByPath = React.useMemo(() => {
    const m = {};
    for (const it of itemsSrc) {
      if (it.section) continue;
      if (it.path) m[it.path] = it;
      if (Array.isArray(it.children)) for (const c of it.children) if (c.path) m[c.path] = c;
    }
    return m;
  }, [itemsSrc]);
  const FavStar = ({ path }) => {
    if (!path || path === '/dashboard' || path === '/actions' || path === '/tableau-de-bord') return null;
    const on = favSet.has(path);
    return (
      <button type="button" className={`da-nav-fav ${on ? 'is-on' : ''}`} aria-pressed={on}
        title={on ? 'Retirer des favoris' : 'Ajouter aux favoris'} aria-label={on ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        data-testid={`sidebar-fav-${path.replace(/\//g, '')}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(path); }}>
        <Star size={13} fill={on ? 'currentColor' : 'none'} />
      </button>
    );
  };
  const desktopNavRef = useRef(null);
  const mobileNavRef = useRef(null);
  // Point 01.1 · l'entrée de la page ouverte reste visible : si elle est hors vue, on la centre (sans bouger le reste de la page).
  const scrollActiveIntoView = useCallback((navEl, smooth = true) => {
    if (!navEl) return;
    const run = () => {
      const active = navEl.querySelector('.da-nav-row.is-child.is-active') || navEl.querySelector('.da-nav-row.is-active');
      if (!active) return;
      const navRect = navEl.getBoundingClientRect();
      const aRect = active.getBoundingClientRect();
      if (aRect.top < navRect.top + 4 || aRect.bottom > navRect.bottom - 4) {
        const delta = (aRect.top - navRect.top) - (navEl.clientHeight / 2 - aRect.height / 2);
        navEl.scrollTo({ top: Math.max(0, Math.min(navEl.scrollHeight, navEl.scrollTop + delta)), behavior: smooth ? 'smooth' : 'auto' });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, []);
  const tenantSlug = paramTenantSlug || contextTenantSlug;
  const basePath = tenantSlug ? `/${tenantSlug}` : '';
  const [level, setLevel] = useState(null); // { name, emoji, tier } from /quality-score
  const [accountOpen, setAccountOpen] = useState(false);
  const [etabOpen, setEtabOpen] = useState(false);
  const roleLabel = user?.role === 'owner' ? 'Direction' : user?.role === 'manager' ? 'Manager' : user?.role === 'communication' ? 'Communication' : user?.role || 'Équipier';
  const [editing, setEditing] = useState(false);
  const canCustomize = ['owner', 'manager'].includes(user?.role);
  const navStyle = useNavStyle();
  const overrides = tenant?.branding?.nav_objects || {};
  const [pickTarget, setPickTarget] = useState(null);
  const iconProps = { overrides, style: navStyle, editing: editing && navStyle === 'objets', onEdit: setPickTarget };

  // Fetch quality score level (gamification badge)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchLevel = async () => {
      try {
        const API = getApiUrl();
        const res = await axios.get(`${API}/quality-score`, { withCredentials: true });
        if (!cancelled && res.data?.level) setLevel(res.data.level);
      } catch {
        // silent
      }
    };
    fetchLevel();
    return () => { cancelled = true; };
  }, [user]);

  // 360+ : les 6 modules du palier restent visibles mais verrouillés (cadenas) tant que 360+ n'est pas actif
  const { locked: plusLocked } = usePlus360();
  const [lockedModule, setLockedModule] = useState(null);

  // Modules en option : un item avec addonKey n'apparaît que si l'option est payée
  const [addonsOn, setAddonsOn] = useState({});
  useEffect(() => {
    if (!user) return;
    const keys = [...new Set(itemsSrc.flatMap((i) => [i, ...(i.children || [])]).map((i) => i.addonKey).filter(Boolean))];
    keys.forEach((k) => axios.get(`${getApiUrl()}/addons/${k}/status`, { withCredentials: true }).then((r) => setAddonsOn((a) => ({ ...a, [k]: !!r.data?.enabled }))).catch(() => setAddonsOn((a) => ({ ...a, [k]: false }))));
  }, [user]);

  // Point 52 · compteurs d'actions du menu = ceux des pages (même endpoint pour toutes les rubriques)
  const [navCounts, setNavCounts] = useState({});
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = () => axios.get(`${getApiUrl()}/nav/compteurs`, { withCredentials: true }).then((r) => { if (!cancelled) setNavCounts(r.data || {}); }).catch(() => {});
    const t0 = setTimeout(load, 1500);  // LOT 47 · Z20 · après les chiffres de la page
    const id = setInterval(load, 90000);
    return () => { cancelled = true; clearInterval(id); clearTimeout(t0); };
  }, [user]);

  // Get tenant branding
  const tenantName = tenant?.name || tenant?.full_name || '360 Resto';
  const tenantTagline = tenant?.tagline || '360 Resto v4.1';
  const primaryColor = tenant?.branding?.primary_color || tenant?.primary_color || 'var(--brand)';

  // Rubriques ordonnées (ordre personnalisé) + filtrage rôle/permissions
  const navOrder = tenant?.branding?.nav_order || null;
  const saveBranding = useBrandingSave();
  const sections = React.useMemo(() => buildSections(menuV2 ? null : navOrder, itemsSrc), [navOrder, menuV2]);
  const persistOrder = (secs) => saveBranding('nav_order', { sections: secs.map((x) => x.key), items: Object.fromEntries(secs.map((x) => [x.key, x.items.map(itemKey)])) }, 'Ordre du menu enregistré');
  const moveSection = (i, dir) => { const n = move(sections, i, dir); if (n) persistOrder(n); };
  const moveItem = (si, ii, dir) => { const n = move(sections[si].items, ii, dir); if (n) persistOrder(sections.map((x, j) => (j === si ? { ...x, items: n } : x))); };
  const visibleItems = React.useMemo(() => {
    const effectiveUser = user || { role: 'serveur' };
    const result = [];
    sections.forEach((sec, si) => {
      const isFav = menuV2 && sec.key === 'favoris';
      const secItems = isFav ? favPaths.map((p) => moduleByPath[p]).filter(Boolean) : sec.items;
      const kept = [];
      secItems.forEach((item, ii) => {
        if (Array.isArray(item.children)) {
          const visibleChildren = item.children.filter((c) => canAccess(effectiveUser, c.path) && (!c.addonKey || addonsOn[c.addonKey]));
          if (visibleChildren.length) kept.push({ ...item, children: visibleChildren, _si: si, _ii: ii, _fav: isFav });
        } else if (canAccess(effectiveUser, item.path) && (!item.addonKey || addonsOn[item.addonKey])) kept.push({ ...item, _si: si, _ii: ii, _fav: isFav });
      });
      if (!kept.length) return;
      result.push({ section: sec.key, title: sec.title, _si: si });
      result.push(...kept);
    });
    return result;
  }, [user, sections, addonsOn, menuV2, favPaths, moduleByPath]);

  // Track open/closed state of collapsible groups (default: auto-open if a child route matches)
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (label) => setOpenGroups((s) => ({ ...s, [label]: !s[label] }));
  const isGroupOpenByDefault = (children, groupPath) => {
    const path = window.location.pathname;
    return children.some((c) => path.includes(c.path)) || (!!groupPath && path.endsWith(groupPath));
  };

  // Point 01.1 · à chaque changement de page : garder la rubrique de la page active ouverte, puis centrer l'entrée active si elle est hors vue (desktop + mobile). S'applique aussi au chargement direct / rechargement.
  useEffect(() => {
    const grp = itemsSrc.find((it) => Array.isArray(it.children) && it.children.some((c) => location.pathname.includes(c.path)));
    if (grp) setOpenGroups((s) => (s[grp.label] === false ? { ...s, [grp.label]: true } : s));
    const t = setTimeout(() => scrollActiveIntoView(desktopNavRef.current), 70);
    return () => clearTimeout(t);
  }, [location.pathname, scrollActiveIntoView]);

  // Mobile · à l'ouverture du menu, le volet s'ouvre sur l'entrée active.
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => scrollActiveIntoView(mobileNavRef.current, false), 120);
    return () => clearTimeout(t);
  }, [isOpen, location.pathname, scrollActiveIntoView]);

  const renderNavItems = (isMobile = false) => {
    return visibleItems.map((item, idx) => {
      // Section header
      if (item.section) {
        if (!item.title && !editing) return null;
        return (
          <div key={idx} className={`da-nav-section ${editing ? 'is-editing' : ''}`} data-testid={`sidebar-section-${item.section}`}>
            <p>{item.title || 'Accueil'}</p>
            {editing && !isMobile && <OrderArrows testid={`sidebar-section-${item.section}`} onUp={item._si > 0 ? () => moveSection(item._si, -1) : null} onDown={item._si < sections.length - 1 ? () => moveSection(item._si, 1) : null} />}
          </div>
        );
      }
      const arrows = editing && !isMobile && !item._fav ? <OrderArrows small testid={`sidebar-order-${itemKey(item).replace(/[^a-z0-9]+/gi, '-')}`} onUp={item._ii > 0 ? () => moveItem(item._si, item._ii, -1) : null} onDown={item._ii < sections[item._si].items.length - 1 ? () => moveItem(item._si, item._ii, 1) : null} /> : null;

      // Collapsible group (parent + children)
      if (Array.isArray(item.children)) {
        const isOpen = openGroups[item.label] !== undefined
          ? openGroups[item.label]
          : isGroupOpenByDefault(item.children, item.path);
        const hasActiveChild = item.children.some(
          (c) => window.location.pathname.includes(`${basePath}${c.path}`),
        );
        // R4 · badge du titre = total des sous-rubriques ; R1 · le titre ouvre le tableau de bord de la rubrique, le chevron replie/déplie
        const groupCount = navCounts[`group:${item.label}`];
        const groupSlug = item.label.toLowerCase().replace(/\s+/g, '-');
        const groupActive = hasActiveChild || (item.path && window.location.pathname === `${basePath}${item.path}`);
        return (
          <div key={`group-${item.label}`} className="mb-0.5" data-testid={`sidebar-group-${groupSlug}`}>
            <div className={`da-nav-row da-nav-group w-full ${groupActive ? 'is-active' : ''}`} data-testid={`sidebar-group-toggle-${groupSlug}`}>
              {item.path ? (
                <NavLink to={`${basePath}${item.path}`} onClick={() => { setOpenGroups((s) => ({ ...s, [item.label]: true })); if (isMobile) onClose?.(); }} className="da-nav-group-link" title={`Tableau de bord · ${item.label}`} data-testid={`sidebar-link-${item.path.replace('/', '')}`}>
                  <NavIcon item={item} idx={idx} {...iconProps} />
                  <span className="flex-1 text-left">{item.label}</span>
                </NavLink>
              ) : (
                <button type="button" onClick={() => toggleGroup(item.label)} className="da-nav-group-link">
                  <NavIcon item={item} idx={idx} {...iconProps} />
                  <span className="flex-1 text-left">{item.label}</span>
                </button>
              )}
              {arrows}
              <NavBadge count={groupCount} to={`${basePath}/actions?rubrique=${encodeURIComponent(`group:${item.label}`)}`} testId={`sidebar-badge-group-${groupSlug}`} />
              <button type="button" onClick={() => toggleGroup(item.label)} className="da-nav-group-toggle" aria-label={isOpen ? 'Replier' : 'Déplier'} aria-expanded={isOpen} data-testid={`sidebar-group-chevron-${groupSlug}`}>
                <ChevronRight size={14} className={`text-[var(--texte-2)] transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>
            </div>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                >
                  {item.children.map((child, ci) => {
                    const childLocked = child.plusKey && plusLocked && child.path !== '/integrations';
                    if (childLocked) {
                      return (
                        <button
                          type="button"
                          key={child.path}
                          onClick={() => setLockedModule(child.plusKey)}
                          className="da-nav-row is-child is-locked w-full text-left"
                          aria-label={`${child.label} · ${child.soon ? 'bientôt disponible, ' : ''}inclus dans 360+`}
                          title={child.soon ? 'Bientôt · inclus dans 360+' : 'Inclus dans 360+'}
                          data-testid={`sidebar-locked-${child.plusKey}`}
                        >
                          <NavIcon item={child} small idx={idx + ci + 1} {...iconProps} />
                          <span className="flex-1 min-w-0">{child.label}{child.hint && <small className="block text-xs font-normal text-[var(--texte-2)] leading-tight truncate">{child.hint}</small>}</span>
                          {child.soon
                            ? <span className="da-nav-soon inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-[var(--p-8a6100)] bg-[var(--p-f7c948)] px-1.5 py-0.5 rounded" data-testid={`sidebar-soon-${child.plusKey}`}><Hourglass size={10} /> Bientôt</span>
                            : <span className="da-nav-lockwrap" aria-hidden="true" data-keepicon="1"><Plus360Mark size={9} plate={false} className="da-nav-lockmark" /><span className="da-nav-lock"><Lock /></span></span>}
                        </button>
                      );
                    }
                    return (
                      <NavLink
                        key={child.path}
                        to={`${basePath}${child.path}`}
                        onClick={isMobile ? onClose : undefined}
                        className={({ isActive }) => `da-nav-row is-child ${isActive ? 'is-active' : ''}`}
                        data-testid={`sidebar-link-${child.path.replace('/', '')}`}
                      >
                        <NavIcon item={child} small idx={idx + ci + 1} {...iconProps} />
                        <span className="flex-1 min-w-0">{child.label}{child.hint && <small className="block text-xs font-normal text-[var(--texte-2)] leading-tight truncate">{child.hint}</small>}{child.soon && <span className="da-nav-soon mt-0.5 inline-flex w-fit items-center gap-1 text-xs uppercase tracking-wider font-bold text-[var(--p-8a6100)] bg-[var(--p-f7c948)] px-1.5 py-0.5 rounded" style={{ display: 'flex' }} data-testid={`sidebar-soon-${child.plusKey || child.path.replace(/[/?=]/g, '-')}`}><Hourglass size={10} /> Bientôt</span>}</span>
                        <span className="da-nav-star-col" style={{ width: 20, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}><FavStar path={child.path} /></span>
                        <span className="da-nav-count-col" style={{ minWidth: 24, display: 'inline-flex', justifyContent: 'flex-end', flexShrink: 0 }}><NavBadge count={navCounts[child.path]} to={`${basePath}/actions?rubrique=${encodeURIComponent(child.path)}`} testId={`sidebar-badge-${child.path.replace('/', '')}`} /></span>
                      </NavLink>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      }

      // Nav item
      // Detect external links (e.g., /public assets like social-slides.html)
      const isExternal = item.path?.startsWith('__EXTERNAL__');
      if (isExternal) {
        const href = item.path.replace('__EXTERNAL__', '');
        return (
          <a
            key={item.path}
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={isMobile ? onClose : undefined}
            className="da-nav-row"
            data-testid={`sidebar-external-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <NavIcon item={item} idx={idx} {...iconProps} />
            <span className="flex-1">{item.label}</span>
            <span className="text-xs uppercase tracking-wider font-bold text-[var(--encre)] px-1.5 py-0.5 rounded bg-[var(--p-fde3d8)] border border-[var(--p-efa3b1)]">PDF</span>
          </a>
        );
      }
      const fullPath = `${basePath}${item.path}`;
      if (item.plusKey && plusLocked && item.path !== '/integrations') {
        return (
          <button
            type="button"
            key={item.path}
            onClick={() => setLockedModule(item.plusKey)}
            className="da-nav-row is-locked"
            aria-label={`${item.label} · ${item.soon ? 'bientôt disponible, ' : ''}inclus dans 360+`}
            title={item.soon ? 'Bientôt · inclus dans 360+' : 'Inclus dans 360+'}
            data-testid={`sidebar-locked-${item.path.replace('/', '')}`}
          >
            <NavIcon item={item} idx={idx} {...iconProps} />
            <span className="flex-1 min-w-0 text-left">{item.label}{item.hint && <small className="block text-xs font-normal text-[var(--texte-2)] leading-tight truncate">{item.hint}</small>}</span>
            {arrows}
            {item.soon
              ? <span className="da-nav-soon inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold text-[var(--p-8a6100)] bg-[var(--p-f7c948)] px-1.5 py-0.5 rounded" data-testid={`sidebar-soon-${item.plusKey}`}><Hourglass size={10} /> Bientôt</span>
              : <span className="da-nav-lockwrap" aria-hidden="true" data-keepicon="1"><Plus360Mark size={9} plate={false} className="da-nav-lockmark" /><span className="da-nav-lock"><Lock /></span></span>}
          </button>
        );
      }

      return (
        <NavLink
          key={item.path + (item.dup ? '-admin' : '')}
          to={fullPath}
          onClick={isMobile ? onClose : undefined}
          className={({ isActive }) => `da-nav-row ${isActive ? 'is-active' : ''}`}
          data-testid={item.appStyle === 'hub-integrations' ? 'sidebar-hub-integrations' : `sidebar-link-${item.path.replace('/', '').replace(/\//g, '-') || 'root'}${item.dup ? '-admin' : ''}`}
        >
          <NavIcon item={item} idx={idx} {...iconProps} />
          <span className="flex-1 min-w-0">{item.label}{item.path === '/mon-abonnement' && <BillingNavBadge tenantId={tenantSlug} testid={`sidebar-subscription-mode-${isMobile?'mobile':'desktop'}`}/>}</span>
          {arrows}
          {item.plusKey && plusLocked && (
            <span className="da-nav-lockwrap" data-keepicon="1" title="Inclus dans 360+" data-testid={`sidebar-lock-${item.path.replace('/', '')}`}><Plus360Mark size={9} plate={false} className="da-nav-lockmark" /><span className="da-nav-lock"><Lock /></span></span>
          )}
          {item.soon && (
            <span className="da-nav-soon" data-testid="sidebar-soon-badge">Bientôt</span>
          )}
          <span className="da-nav-star-col" style={{ width: 20, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}><FavStar path={item.path} /></span>
          <span className="da-nav-count-col" style={{ minWidth: 24, display: 'inline-flex', justifyContent: 'flex-end', flexShrink: 0 }}><NavBadge count={navCounts[item.path]} to={item.path === '/actions' ? `${basePath}/actions?filtre=urgent` : item.path === '/taches' ? `${basePath}/taches` : `${basePath}/actions?rubrique=${encodeURIComponent(item.path)}`} testId={`sidebar-count-${item.path.replace('/', '')}`} /></span>
        </NavLink>
      );
    });
  };

  return (
    <>
      <Plus360LockModal moduleKey={lockedModule} onClose={() => setLockedModule(null)} />
      {/* Desktop Sidebar - Always visible on lg+ */}
      {!mobileOnly && <aside className={`da-sidebar hidden md:flex fixed left-0 top-0 h-full w-72 border-r border-[rgba(17,17,17,.08)] flex-col z-40 ${collapsed ? 'is-collapsed' : ''}`} data-testid="desktop-sidebar" aria-hidden={collapsed || undefined}>
        {/* 01.1 · Ligne 1 = établissement (logo, nom, pays · devise, chevron) + bouton de rétractation noir DANS le bandeau */}
        <div className="pt-3 pb-3 px-3 border-b border-[var(--bord)] flex-shrink-0">
          <SiteSwitcher
            tenant={tenant}
            tenantName={tenantName}
            meta={[tenant?.city || tenant?.ville, tenant?.country ? new Intl.DisplayNames(['fr'], { type: 'region' }).of(tenant.country) : null, tenant?.currency].filter(Boolean).join(' · ') || tenantTagline}
            primaryColor={primaryColor}
            open={etabOpen}
            onOpenChange={(v) => { setEtabOpen(v); if (v) setAccountOpen(false); }}
          />
        </div>

        {/* User info — Apple-style compact (V6.0.83 reduced) */}
        <div className="px-4 py-3 border-b border-[var(--bord)] flex-shrink-0">
          {/* Point 52.2 · « Prénom Nom · Direction » ; clic → Mon profil, mot de passe, Déconnexion (plus de point vert ni de bouton en bas) */}
          <div className="relative">
            <button type="button" onClick={() => { setAccountOpen((v) => !v); setEtabOpen(false); }} aria-expanded={accountOpen} aria-haspopup="menu" title="Mon compte" data-testid="sidebar-account-btn" className="sb-account w-full flex items-center gap-2.5 text-left">
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--sur-couleur)] text-[12px] font-semibold shrink-0" style={{ background: primaryColor }}>{user?.name ? user.name.charAt(0).toUpperCase() : 'U'}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-[var(--texte)] truncate leading-tight" data-testid="sidebar-account-name">{user?.name && user.name.trim().toLowerCase() !== roleLabel.toLowerCase() ? user.name : (user?.email?.split('@')[0] || 'Mon compte')} · {roleLabel}</span>
              </span>
              <ChevronDown size={16} className={`text-[var(--texte-2)] shrink-0 transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
            </button>
            {/* Deux informations distinctes : niveau Resto (établissement) · style des pictogrammes. Clic → ouvre « Mon compte ». */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="sidebar-badges">
              {level && <button type="button" onClick={() => { setAccountOpen(true); setEtabOpen(false); }} data-testid="sidebar-level-badge" className="chip-kit chip-kit-corail" style={{ minHeight: 22, fontSize: 12 }} title={level.subtitle || `Niveau de l'établissement · ${level.name}`}><Award size={13} /> Niveau {level.tier} / 4</button>}
              <button type="button" onClick={() => { setAccountOpen(true); setEtabOpen(false); }} data-testid="nav-style-pastille" className="chip-kit" style={{ minHeight: 22, fontSize: 12 }} title="Pictogrammes du menu">{navStyle === 'objets' ? <><img src="/brand/home/orange_whole.webp" alt="" style={{ width: 13, height: 13 }} /> Objets</> : <><KitBadge name="home" size={13} /> Classiques</>}</button>
            </div>
            {accountOpen && <>
              <div className="fixed inset-0 z-40" onClick={() => setAccountOpen(false)} aria-hidden="true" data-testid="sidebar-account-backdrop" />
              <div role="menu" className="absolute left-0 right-0 top-full mt-1 z-50 bg-[var(--carte)] border border-[var(--bord)] rounded-xl shadow-lg p-1" data-testid="sidebar-account-menu">
              {/* 01.1/01.4 · identité + e-mail (jamais affichés en permanence dans la colonne) */}
              <p className="px-3 pt-1.5 pb-0.5 text-[13px] font-semibold text-[var(--texte)] truncate">{user?.name || 'Mon compte'} · {roleLabel}</p>
              {user?.email && <p className="px-3 pb-1.5 text-xs text-[var(--texte-2)] truncate" data-testid="sidebar-account-email">{user.email}</p>}
              {level && <button type="button" role="menuitem" className="sb-menu-item flex items-center justify-between" onClick={() => { setAccountOpen(false); navigate(`${basePath}/progression`); }} data-testid="sidebar-account-level">
                <span className="inline-flex items-center gap-1.5"><Award size={14} className="text-[var(--brand)]" /> Niveau {level.tier} / 4 · {level.name}</span>
                <span className="text-xs text-[var(--brand)] font-semibold">comment progresser ›</span>
              </button>}
              <div className="my-1 mx-2 border-t border-[var(--bord)]" />
              {/* 01.2/01.3 · Affichage : pictogrammes du menu (segmenté) + personnaliser */}
              <p className="px-3 pt-0.5 pb-1 text-xs uppercase tracking-wider font-semibold text-[var(--texte-2)]">Affichage</p>
              <div className="px-3 pb-1.5">
                <p className="text-[12.5px] font-medium text-[var(--texte-2)] mb-1.5">Pictogrammes du menu</p>
                <div className="inline-flex w-full rounded-xl bg-[var(--remplissage)] p-0.5" role="radiogroup" aria-label="Style des pictogrammes du menu">
                  <button type="button" role="radio" aria-checked={navStyle === 'objets'} onClick={() => setNavStyle('objets')} data-testid="sidebar-account-style-objets" className={`flex-1 inline-flex items-center justify-center py-1.5 rounded-lg text-[13px] font-bold transition-colors ${navStyle === 'objets' ? 'bg-[var(--carte)] shadow-sm text-[var(--texte)]' : 'text-[var(--texte-2)]'}`}>Objets</button>
                  <button type="button" role="radio" aria-checked={navStyle === 'classique'} onClick={() => setNavStyle('classique')} data-testid="sidebar-account-style-classique" className={`flex-1 inline-flex items-center justify-center py-1.5 rounded-lg text-[13px] font-bold transition-colors ${navStyle === 'classique' ? 'bg-[var(--carte)] shadow-sm text-[var(--texte)]' : 'text-[var(--texte-2)]'}`}>Classiques</button>
                </div>
              </div>
              {canCustomize && <button type="button" role="menuitem" className="sb-menu-item" onClick={() => { setAccountOpen(false); setEditing((v) => !v); setPickTarget(null); }} data-testid="sidebar-account-customize">{editing ? 'Terminer la personnalisation' : 'Personnaliser le menu'}</button>}
              <div className="my-1 mx-2 border-t border-[var(--bord)]" />
              <button type="button" role="menuitem" className="sb-menu-item" onClick={() => { setAccountOpen(false); navigate(`${basePath}/dashboard?feuille=moi`); }} data-testid="sidebar-account-profile">Mon compte et sécurité</button>
              <button type="button" role="menuitem" className="sb-menu-item is-danger" onClick={logout} data-testid="sidebar-logout-btn">Se déconnecter</button>
            </div>
            </>}
          </div>
        </div>
        {pickTarget && <NavObjectPicker target={pickTarget} current={overrides[pickTarget.key] || pickTarget.current} onClose={() => setPickTarget(null)} />}

        {/* Navigation - Scrollable */}
        <nav ref={desktopNavRef} className="flex-1 min-h-0 overflow-y-auto py-2 scrollbar-thin">
          {renderNavItems()}
        </nav>

      </aside>}

      {/* Tablet/Mobile Sidebar - Slide out */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/50 z-50"
              onClick={onClose}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="da-sidebar md:hidden fixed left-0 top-0 h-full w-80 z-50 flex flex-col shadow-2xl"
            >
              {/* Header · 01.1 ligne 1 = établissement (switcher) + fermeture */}
              <div className="p-4 border-b border-[var(--bord)] flex items-center justify-between gap-2 flex-shrink-0">
                <div className="flex-1 min-w-0">
                  <SiteSwitcher
                    tenant={tenant}
                    tenantName={tenantName}
                    meta={[tenant?.city || tenant?.ville, tenant?.country ? new Intl.DisplayNames(['fr'], { type: 'region' }).of(tenant.country) : null, tenant?.currency].filter(Boolean).join(' · ') || tenantTagline}
                    primaryColor={primaryColor}
                  />
                </div>
                <button onClick={onClose} className="p-2 hover:bg-[var(--remplissage)] rounded-[14px] shrink-0" data-testid="mobile-sidebar-close-btn" aria-label="Fermer le menu">
                  <X size={24} className="text-[var(--texte-2)]" />
                </button>
              </div>

              {/* User info */}
              <div className="p-4 border-b border-[var(--bord)] flex-shrink-0" style={{ background: `linear-gradient(to right, ${primaryColor}10, ${primaryColor}05)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-[var(--sur-couleur)] font-bold text-lg" style={{ background: `linear-gradient(to bottom right, ${primaryColor}, ${primaryColor}dd)` }}>
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--texte)]">{user?.name?.split(' ')[0] || 'Utilisateur'}</p>
                    <p className="text-sm text-[var(--texte-2)]">{user?.role === 'owner' ? 'Gérant(e)' : user?.role === 'communication' ? 'Communication' : user?.role || 'Employé'}</p>
                  </div>
                </div>
                {level && (
                  <button
                    onClick={() => { onClose && onClose(); navigate(`${basePath}/dashboard`); }}
                    data-testid="mobile-sidebar-level-badge"
                    className={`mt-3 w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      level.tier === 4
                        ? 'bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 text-[var(--p-c43c12)] shadow-md shadow-amber-500/30'
                        : level.tier === 3
                        ? 'bg-gradient-to-r from-emerald-100 to-teal-100 text-[var(--p-1f5a30)]'
                        : level.tier === 2
                        ? 'bg-gradient-to-r from-lime-100 to-emerald-100 text-lime-800'
                        : 'bg-gradient-to-r from-amber-100 to-orange-100 text-[var(--p-c43c12)]'
                    }`}
                  >
                    <span className="text-base leading-none">{level.emoji}</span>
                    <span className="truncate flex-1 text-left">{level.name}</span>
                  </button>
                )}
              </div>

              {/* Navigation - Scrollable */}
              <nav ref={mobileNavRef} className="flex-1 overflow-y-auto py-2">
                {renderNavItems(true)}
              </nav>

              {/* Logout */}
              <div className="p-4 border-t border-[var(--bord)] flex-shrink-0">
                <button
                  onClick={logout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[var(--encre)] hover:bg-[var(--p-fde3d8)] rounded-lg transition-colors"
                  data-testid="mobile-sidebar-logout-btn"
                >
                  <LogOut size={20} />
                  <span className="font-medium">Déconnexion</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default DesktopSidebar;
